<template>
<div>
	<widget title="High Level Performance">

		<div class="row">
			<div class="col-xl-8 col-lg-12">

				<div class="kpi-layout">
					<div class="kpi-lead">
						<info-box
							label="Gross Profit Margin"
							:value="grossProfitMarginPct + ' %'"
							:stacked="true"
							:standout="true"
							:dark="true"
							:large="true"
						></info-box>
					</div>
					<div class="kpi-grid">
						<info-box
							label="Revenue"
							:value="'$ ' + totalRevenue.toCurrency()"
							:stacked="true"
							:standout="true">
						</info-box>
						<info-box
							label="Total Costs (Expense & Labour)"
							:value="'$ ' + totalCostsExpenseLabour.toCurrency()"
							:stacked="true"
							:standout="true">
						</info-box>
						<info-box
							label="Gross Profit"
							:value="'$ ' + grossProfit.toCurrency()"
							:stacked="true"
							:standout="true">
						</info-box>
						<template v-if="project.actual_project_cost > 0">
							<info-box 
								label="Imported Costs from Accounting"
								:value="'$ ' + project.actual_project_cost"
								:stacked="true"
								:standout="true">
							</info-box>
							<info-box
								label="Adjusted Gross Profit"
								:value="'$ ' + adjustedGrossProfit.toCurrency()"
								:stacked="true"
								:standout="true">
							</info-box>
						</template>
					</div>
				</div>
			</div>
			<div class="col-xl-4 col-lg-12 chart-col">
				<p v-if="grossProfitPieTotal <= 0" class="text-center text-muted py-4">No data</p>
				<div v-else class="chart-fill">
					<chart v-if="showChart"
						type="pie"
						:labels="['Revenue', 'Total Costs']"
						:data="grossProfitPieData"
						:background-color="['#5FB3B3', '#7C61F3']"
						:border-width="1"
						:options="grossProfitPieOptions">
					</chart>
				</div>
			</div>
		</div>
	</widget>

	<widget>
		<project-revenue-graph v-if="'id' in project" :project="project"></project-revenue-graph>
	</widget>

	<widget title="Performance vs Budget">

		<div class="row">
			<div class="col-xl-4 col-lg-12 chart-col">
				<p v-if="budgetVsActualBarTotal <= 0" class="text-center text-muted py-4">No data</p>
				<div v-else class="chart-fill">
					<chart v-if="showChart"
						type="bar"
						:labels="budgetVsActualBarLabels"
						:datasets="budgetVsActualBarDatasets"
						:options="budgetVsActualBarOptions"
						:height="280">
					</chart>
				</div>
			</div>
			<div class="col-xl-8 col-lg-12">
				<div class="kpi-layout">
					<div class="kpi-lead">
						<info-box 
							label="% Project Complete" 
							:value="projectCompletePct"
							:stacked="true" 
							:dark="true"
							:large="true"
							:standout="true">
						</info-box>
						
					</div>
					<div class="kpi-grid">

						<info-box 
							label="Revenue vs. Budgeted Revenue" 
							:value="actualVsBudgetedRevenue"
							:stacked="true" 
							:standout="true">
						</info-box>

						<info-box 
							label="Total Labour vs. Budgeted Labour" 
							:value="actualVsBudgetedLabour"
							:stacked="true" 
							:standout="true">
						</info-box>

						<info-box 
							label="Total Expenses vs. Budgeted Expenses (material + equipment costs)" 
							:value="actualVsBudgetedExpenses"
							:stacked="true" 
							:standout="true">
						</info-box>

						<info-box 
							label="Supplier Quote vs. Budgeted Supplier Quote" 
							:value="supplierQuoteVsBudgetedSupplierQuote"
							:stacked="true" 
							:standout="true">
						</info-box>

						<info-box 
							label="Gross Profit vs. Budgeted Gross Profit" 
							:value="actualVsBudgetedGrossProfit"
							:secondary-value="grossProfitPercentComplete"
							:stacked="true" 
							:standout="true">
						</info-box>
						
					</div>
				</div>
			</div>
		</div>
	</widget>

	<widget title="Billing">
		<div class="row">
			<div class="col-xl-4 col-lg-12">
				Progress bar here of Completed to Date %
			</div>
			<div class="col-xl-8 col-lg-12">
				<div class="kpi-layout">
					<div class="kpi-lead">
						<info-box 
							label="Comp. to Date" 
							class="mr-1" 
							:value="billingCompletedToDatePct" 
							:stacked="true" 
							:standout="true"
							:dark="true"
							:large="true">
						</info-box>
					</div>
					<div class="kpi-grid">
						<info-box 
							label="Org. Project" 
							:value="'$ ' + billingOriginalProjectValue.toCurrency()" 
							class="mr-1" 
							:stacked="true" 
							:standout="true">
						</info-box>
						<info-box 
							label="Change Order"  
							class="mr-1"
							:value="'$ ' + billingTotalChangeOrderValue.toCurrency()" 
							:stacked="true" 
							:standout="true">
						</info-box>
						<info-box 
							label="Project Total"
							class="mr-1" 
							:value="'$ ' + billingTotalProjectValue.toCurrency()" 
							:stacked="true" 
							:standout="true">
						</info-box>
						<info-box 
							label="Comp. to Date" 
							class="mr-1" 
							:value="'$ ' + billingCompletedToDateValue.toCurrency()" 
							:stacked="true" 
							:standout="true">
						</info-box>
						
						<info-box 
							label="Remaining" 
							class="mr-1" 
							:value="'$ ' + billingRemainingValue.toCurrency()" 
							:stacked="true" 
							:standout="true">
						</info-box>
						<info-box 
							label="Holdback" 
							class="mr-1" 
							:value="'$ ' + holdbackValue.toCurrency()" 
							:stacked="true" 
							:standout="true">
						</info-box>
					</div>
				</div>
			</div>
		</div>
	</widget>

	<widget title="Expenses">
		<div class="row">
			<div class="col-xl-8 col-lg-12">
				<div class="kpi-layout">
					<div class="kpi-lead">
						<info-box label="Total Expenses" :value="'$ ' + expenseReportValuesSafe.subtotal_sum.toCurrency()" :stacked="true" :standout="true" :dark="true" :large="true"></info-box>
						<!--
						<info-box label="Grand Total" :value="'$ ' + expenseReportValuesSafe.grand_total_sum.toCurrency()" class="mr-1" :stacked="true" :standout="true" :dark="true" :large="true"></info-box>-->
					</div>
					<div class="kpi-grid kpi-grid--2rows">
						<info-box label="Average" :value="'$ ' + expenseReportValuesSafe.average_expense_total.toCurrency()" :stacked="true" :standout="true"></info-box>
						<info-box label="Average Tip" :value="+expenseReportValuesSafe.average_tip_pct.toFixed(1) + ' %'" :stacked="true" :standout="true"></info-box>
						<info-box label="Count" :value="expenseReportValuesSafe.record_count" :stacked="true" :standout="true"></info-box>
					</div>
				</div>
			</div>
			<div class="col-xl-4 col-lg-12 chart-col">
				<p v-if="expenseDoughnutTotal <= 0" class="text-center text-muted py-4">No data</p>
				<div v-else class="chart-fill">
					<chart v-if="showChart"
						type="doughnut"
						:labels="['Total', 'Tax & Tip']"
						:data="expenseDoughnutData"
						:background-color="['#5FB3B3', '#9A8CF2']"
						:border-width="1"
						:options="expenseDoughnutOptions">
					</chart>
				</div>
			</div>
		</div>
	</widget>

	<widget title="Purchase Orders">
		<div class="row">
			<div class="col-xl-4 col-lg-12 chart-col">
				<p v-if="poPieTotal <= 0" class="text-center text-muted py-4">No data</p>
				<div v-else class="chart-fill">
					<chart v-if="showChart"
						type="doughnut"
						:labels="['Invoiced', 'Outstanding']"
						:data="poPieData"
						:background-color="['#5FB3B3', '#7C61F3']"
						:border-width="1"
						:options="poPieOptions">
					</chart>
				</div>
			</div>
			<div class="col-xl-8 col-lg-12">
				<div class="kpi-layout">
					<div class="kpi-lead">
						<info-box label="Grand Total" :value="'$ ' + poReportValuesSafe.grand_total_sum.toCurrency()" class="mr-1" :stacked="true" :standout="true" :dark="true" :large="true"></info-box>
					</div>
					<div class="kpi-grid kpi-grid--2rows">
						<info-box label="Total PO Value" :value="'$ ' + poReportValuesSafe.grand_total_sum.toCurrency()" :stacked="true" :standout="true"></info-box>
						<info-box label="Invoice Value" :value="'$ ' + totalPOInvoiceValue.toCurrency()" :stacked="true" :standout="true"></info-box>
						<info-box label="Diff." :value="'$ ' + (poReportValuesSafe.grand_total_sum - totalPOInvoiceValue).toCurrency()" :stacked="true" :standout="true"></info-box>
						<info-box label="No. Purchase Orders" :value="poReportValuesSafe.record_count" :stacked="true" :standout="true"></info-box>
					</div>
				</div>
			</div>
		</div>
	</widget>

</div>
</template>
<script>
import Widget from "@/components/Widget"
import TableList from "@/components/TableList"
import InfoBox from "@/components/InfoBox"
import InvoiceMixins from "@/mixins/InvoiceMixins.js"
import ProjectBidMixins from "@/mixins/ProjectBidMixins.js"
import Chart from "@/components/Chart"
import ProjectRevenueGraph from "@/components/super_components/ProjectRevenueGraph"

export default {
	name: "ProjectReportWidgets",
	components: {
		Widget,
		TableList,
		InfoBox,
		Chart,
		ProjectRevenueGraph
	},
	mixins: [InvoiceMixins, ProjectBidMixins],
	props: [
		"project"
	],
	data() {
		return {
			showChart: false,
			expenseReportValues: null,
			labourTotalCost: 0,
			totalRevenue: 0,
			budgetedCostAll: 0,
			totalPOValue: 0,
			poReportValues: null,
			totalPOInvoiceValue: 0,
			totalChangeOrderValue: 0,
			budgetedSupplierQuotes: 0,
			budgetedMaterialsEquipment: 0,
			budgetedLabour: 0,
			grossProfitPieOptions: {
				responsive: true,
				maintainAspectRatio: false,
				legend: { position: 'bottom' },
				tooltips: {
					callbacks: {
						label(tooltipItem, data) {
							const label = data.labels[tooltipItem.index] || ''
							const value = data.datasets[0].data[tooltipItem.index] || 0
							return `${label}: $${Number(value).toLocaleString()}`
						}
					}
				}
			}
		}
	},
	watch: {
		project() {
			if (this.project && "id" in this.project) {
				this.fetchData()
			}
		}
	},
	computed: {
		projectId() {
			return this.project && this.project.id
		},
		expenseReportValuesSafe() {
			return this.expenseReportValues || {
				subtotal_sum: 0,
				grand_total_sum: 0,
				average_expense_total: 0,
				average_tip_pct: 0,
				record_count: 0
			}
		},
		poReportValuesSafe() {
			return this.poReportValues || {
				grand_total_sum: 0,
				record_count: 0
			}
		},
		totalCostsExpenseLabour() {
			const total = +this.expenseReportValuesSafe.subtotal_sum + +this.labourTotalCost
			return total
		},
		grossProfit() {
			console.log(this.totalRevenue - +this.totalCostsExpenseLabour,this.totalRevenue, +this.totalCostsExpenseLabour)
			return +this.totalRevenue - +this.totalCostsExpenseLabour
		},
		adjustedGrossProfit() {
			return +this.totalRevenue - +this.project.actual_project_cost
		},
		grossProfitMarginPct() {
			if (!this.totalRevenue || this.totalRevenue <= 0) return "0.00"
			return ((this.grossProfit / this.totalRevenue) * 100).toFixed(2)
		},
		grossProfitPieData() {
			return [+this.totalRevenue, +this.totalCostsExpenseLabour]
		},
		grossProfitPieTotal() {
			return +this.totalRevenue + +this.totalCostsExpenseLabour
		},
		activeBid() {
			if (!this.project || !this.project.JobBid || !Array.isArray(this.project.JobBid))
				return null
			return this.project.JobBid.find(b => b.is_active == 1) || null
		},
		budgetedRevenue() {
			const bidTotal = this.activeBid != null && this.activeBid.grand_total != null ? +this.activeBid.grand_total : 0

			return bidTotal + +this.totalChangeOrderValue
		},
		actualVsBudgetedRevenue() {
			const actual = (typeof this.totalRevenue === 'number' ? this.totalRevenue : +this.totalRevenue).toCurrency()
			const budgeted = this.activeBid != null ? this.budgetedRevenue.toCurrency() : '—'
			return '$ ' + actual + ' | $ ' + budgeted
		},
		actualProjectCost() {
			const v = this.project && this.project.actual_project_cost != null ? this.project.actual_project_cost : null
			return v != null ? +v : null
		},
		supplierQuoteVsBudgetedSupplierQuote() {
			const actual = (typeof this.totalPOValue === 'number' ? this.totalPOValue : +this.totalPOValue).toCurrency()
			const budgeted = this.activeBid != null ? this.budgetedSupplierQuotes.toCurrency() : '—'
			return '$ ' + actual + ' | $ ' + budgeted
		},
		actualVsBudgetedExpenses() {
			const actual = this.totalCostsExpenseLabour.toCurrency()
			const budgeted = this.activeBid != null ? this.budgetedMaterialsEquipment.toCurrency() : '—'
			return '$ ' + actual + ' | $ ' + budgeted
		},
		actualVsBudgetedLabour() {
			const actual = (typeof this.labourTotalCost === 'number' ? this.labourTotalCost : +this.labourTotalCost).toCurrency()
			const budgeted = this.activeBid != null ? this.budgetedLabour.toCurrency() : '—'
			return '$ ' + actual + ' | $ ' + budgeted
		},
		budgetedGrossProfit() {
			if (this.activeBid == null) return 0

				console.log(this.budgetedCostAll, this.budgetedRevenue)
			return +this.budgetedRevenue - +this.budgetedCostAll
		},
		actualVsBudgetedGrossProfit() {
			// Gross Profit = Revenue - Expenses - Labour cost
			const actual = (typeof this.grossProfit === 'number' ? this.grossProfit : +this.grossProfit).toCurrency()
			const budgeted = this.activeBid != null ? this.budgetedGrossProfit.toCurrency() : '—'
			return '$ ' + actual + ' | $ ' + budgeted
		},
		grossProfitPercentComplete() {
			if (this.activeBid == null || this.budgetedGrossProfit <= 0) return '—'
			const pct = (this.grossProfit / this.budgetedGrossProfit) * 100
			return pct.toFixed(1) + ' %'
		},
		projectCompletePct() {
			if (!this.budgetedRevenue || this.budgetedRevenue <= 0) return '—'
			const pct = (this.totalRevenue / this.budgetedRevenue) * 100
			return pct.toFixed(1) + ' %'
		},
		// Budget vs Actual bar chart: 3 categories (Revenue, Cost, Gross Profit), 2 bars each (Performance, Budget)
		budgetVsActualBarLabels() {
			return ['Revenue', 'Cost', 'Gross Profit']
		},
		budgetVsActualBarDatasets() {
			let data = [
				{
					label: 'Performance',
					data: [+this.totalRevenue, +this.totalCostsExpenseLabour, +this.grossProfit],
					backgroundColor: '#5FB3B3',
					borderWidth: 1
				},
				{
					label: 'Budget',
					data: [+this.budgetedRevenue, +this.budgetedCostAll, +this.budgetedGrossProfit],
					backgroundColor: '#7C61F3',
					borderWidth: 1
				}
			]

			return data
		},
		budgetVsActualBarTotal() {
			return +this.totalRevenue + +this.totalCostsExpenseLabour + +this.grossProfit + +this.budgetedRevenue + +this.budgetedCostAll + +this.budgetedGrossProfit
		},
		budgetVsActualBarOptions() {
			return {
				responsive: true,
				maintainAspectRatio: false,
				legend: { position: 'bottom' },
				scales: {
					xAxes: [{ stacked: false }],
					yAxes: [{
						stacked: false,
						ticks: {
							beginAtZero: true,
							callback(value) {
								return '$ ' + Number(value).toLocaleString()
							}
						}
					}]
				},
				tooltips: {
					callbacks: {
						label(tooltipItem, data) {
							const label = data.labels[tooltipItem.index] || 'Amount'
							const value = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index] || 0
							return label + ': $' + Number(value).toLocaleString()
						}
					}
				}
			}
		},
		// Expenses doughnut
		expenseDoughnutData() {
			const sub = +this.expenseReportValuesSafe.subtotal_sum
			const grand = +this.expenseReportValuesSafe.grand_total_sum
			const taxTip = Math.max(0, grand - sub)
			return [sub, taxTip]
		},
		expenseDoughnutTotal() {
			return +this.expenseReportValuesSafe.grand_total_sum
		},
		expenseDoughnutOptions() {
			return {
				responsive: true,
				maintainAspectRatio: false,
				legend: { position: 'bottom' },
				tooltips: {
					callbacks: {
						label(tooltipItem, data) {
							const label = data.labels[tooltipItem.index] || ''
							const value = data.datasets[0].data[tooltipItem.index] || 0
							return `${label}: $${Number(value).toLocaleString()}`
						}
					}
				}
			}
		},
		// Purchase Orders pie/doughnut
		poPieData() {
			const total = +this.poReportValuesSafe.grand_total_sum
			const invoiced = +this.totalPOInvoiceValue
			const outstanding = Math.max(0, total - invoiced)
			return [invoiced, outstanding]
		},
		poPieTotal() {
			return +this.poReportValuesSafe.grand_total_sum
		},
		poPieOptions() {
			return {
				responsive: true,
				maintainAspectRatio: false,
				legend: { position: 'bottom' },
				tooltips: {
					callbacks: {
						label(tooltipItem, data) {
							const label = data.labels[tooltipItem.index] || ''
							const value = data.datasets[0].data[tooltipItem.index] || 0
							return `${label}: $${Number(value).toLocaleString()}`
						}
					}
				}
			}
		},
		// Billing section (schedule of values) – same logic as DrawBreakdown.vue
		billingMutatedTasks() {
			return this.mutateTasks(this.tasks || [])
		},
		billingMutatedChangeOrders() {
			return this.mutateChangeOrders(this.changeOrders || [])
		},
		billingOriginalProjectValue() {
			return this.billingMutatedTasks.reduce((acc, task) => {
				let total = 0
				if ("totalValue" in task) {
					const totalValue = (task.totalValue || "").replaceAll(",", "")
					total = +totalValue
				}
				return acc + (typeof total.round === 'function' ? total.round(2) : Math.round(total * 100) / 100)
			}, 0)
		},
		billingTotalChangeOrderValue() {
			return this.billingMutatedChangeOrders.reduce((acc, co) => {
				let total = 0
				if ("totalValue" in co) {
					const totalValue = (co.totalValue || "").replaceAll(",", "")
					total = +totalValue
				}
				total = typeof total.round === 'function' ? total.round(2) : Math.round(total * 100) / 100
				return acc + total
			}, 0)
		},
		billingTotalProjectValue() {
			return this.billingOriginalProjectValue + this.billingTotalChangeOrderValue
		},
		billingTotalCompletedTasks() {
			const sum = this.billingMutatedTasks.reduce((acc, task) => acc + (task.completedValueRaw != null ? (typeof task.completedValueRaw.round === 'function' ? task.completedValueRaw.round(2) : Math.round(task.completedValueRaw * 100) / 100) : 0), 0)
			return typeof sum.round === 'function' ? sum.round(2) : Math.round(sum * 100) / 100
		},
		billingTotalCompletedChangeOrders() {
			const sum = this.billingMutatedChangeOrders.reduce((acc, co) => acc + (co.completedValueRaw != null ? (typeof co.completedValueRaw.round === 'function' ? co.completedValueRaw.round(2) : Math.round(co.completedValueRaw * 100) / 100) : 0), 0)
			return typeof sum.round === 'function' ? sum.round(2) : Math.round(sum * 100) / 100
		},
		billingCompletedToDateValue() {
			const total = this.billingTotalCompletedChangeOrders + this.billingTotalCompletedTasks
			return typeof total.round === 'function' ? total.round(2) : Math.round(total * 100) / 100
		},
		billingCompletedToDatePct() {
			const totalValue = this.billingTotalProjectValue
			if (!totalValue || totalValue <= 0) return "0%"
			const totalCompleted = this.billingCompletedToDateValue
			if (totalCompleted <= 0) return "0%"
			const pct = (totalCompleted / totalValue) * 100
			return (typeof pct.round === 'function' ? pct.round(1) : Math.round(pct * 10) / 10) + "%"
		},
		billingRemainingValue() {
			const v = this.billingTotalProjectValue - this.billingCompletedToDateValue
			return typeof v.round === 'function' ? v.round(2) : Math.round(v * 100) / 100
		}
	},
	methods: {
		computeBudgetedCostFromFlattened() {
			const elements = ["materials", "equipment", "labour", "supplier-quotes", "additional-factors"]
			let totalCost = 0
			elements.forEach(key => {
				const cost = this.flattenedRecordsFromNested(key).reduce((acc, item) => acc + +item.totalBaseRaw, 0)
				if (!isNaN(cost))
					totalCost += typeof cost.round === 'function' ? cost.round(2) : Math.round(cost * 100) / 100
			})
			console.log(totalCost)
			return totalCost
		},
		computeBudgetedSupplierQuotesFromFlattened() {
			return this.flattenedRecordsFromNested('supplier-quotes').reduce((acc, item) => acc + +item.totalBaseRaw, 0)
		},
		computeBudgetedMaterialsEquipmentFromFlattened() {
			const elements = ["materials", "equipment"]
			let total = 0
			elements.forEach(key => {
				const cost = this.flattenedRecordsFromNested(key).reduce((acc, item) => acc + +item.totalBaseRaw, 0)
				if (!isNaN(cost))
					total += typeof cost.round === 'function' ? cost.round(2) : Math.round(cost * 100) / 100
			})
			return total
		},
		computeBudgetedLabourFromFlattened() {
			const cost = this.flattenedRecordsFromNested('labour').reduce((acc, item) => acc + +item.totalBaseRaw, 0)
			return isNaN(cost) ? 0 : (typeof cost.round === 'function' ? cost.round(2) : Math.round(cost * 100) / 100)
		},
		computeChangeOrderLabour() {
			return (this.changeOrders || []).reduce((acc, co) => {
				let coLabour = 0
				let objects = co.JobChangeOrderObject || co.JobChangeQuoteObject || []
				objects.filter(obj => obj.type === "Labour").forEach(obj => {
					let parsed = {}
					try {
						parsed = JSON.parse(obj.field_values_json || "{}")
					} catch (e) {}
					let lineTotal = (+(parsed.hrs) || 0) * (+(parsed.rate) || 0) * (+(parsed.crew) || 1)
					coLabour += lineTotal
				})
				let coQty = +(co.qty) || 1
				return acc + coLabour * coQty
			}, 0)
		},
		fetchData() {
			let expenseFilters = [["job_id", this.project.id]]
			let timeLogFilters = [["job_id", this.project.id]]
			let poFilters = [["job_id", this.project.id]]

			let changeOrderFilters = [
				["job_id", this.project.id],
				["is_finalized", 1],
				["quote_status", "Accepted"],
			]

			let expensePOFilters = [["job_id", this.project.id], ["purchase_order_id >", 0]]

			let promises = [
				this.getJSON("Expense/report", { filters: expenseFilters }),
				this.getJSON("TimeLog/report", { filters: timeLogFilters }),
				this.fetchProjectInvoicesReport(false),
				this.getJSON("PurchaseOrder/report", { filters: poFilters }),
				this.getJSON("JobChangeOrder/get", { filters: changeOrderFilters }),
				this.getJSON("Expense/report", { filters: expensePOFilters }),
				this.fetchProjectTasks(false),
				this.fetchProjectChangeOrders(false, ["JobChangeOrderObject"]),
				this.fetchProjectInvoices(false, ["JobInvoiceTask"])
			]

			let activeBid = this.project.JobBid && this.project.JobBid.find(b => b.is_active == 1)
			if (activeBid) {
				let prevBidComponentsFlat = this.$store.bidComponentsFlat
				promises.push(
					this.fetchAllComponents(activeBid.id).then(response => {
						if (response.success) {
							this.budgetedCostAll = this.computeBudgetedCostFromFlattened()
							this.budgetedSupplierQuotes = this.computeBudgetedSupplierQuotesFromFlattened()
							this.budgetedMaterialsEquipment = this.computeBudgetedMaterialsEquipmentFromFlattened()
							this.budgetedLabour = this.computeBudgetedLabourFromFlattened()
						}
						this.$store.bidComponentsFlat = prevBidComponentsFlat
						return response
					})
				)
			} else {
				this.budgetedCostAll = 0
				this.budgetedSupplierQuotes = 0
				this.budgetedMaterialsEquipment = 0
				this.budgetedLabour = 0
			}

			Promise.all(promises).then((responses) => {
				let [expenseRes, timeLogRes, invoiceRes, poRes, changeOrderRes, expensePORes] = responses

				if (expenseRes && expenseRes.success && expenseRes.data) {
					this.expenseReportValues = {}
					Object.keys(expenseRes.data).forEach(key => {
						this.expenseReportValues[key] = +expenseRes.data[key]
					})
				}

				if (timeLogRes && timeLogRes.success && timeLogRes.data)
					this.labourTotalCost = +timeLogRes.data.total_calculated_cost

				if (invoiceRes && invoiceRes.success && invoiceRes.data)
					this.totalRevenue = +invoiceRes.data.total_revenue

				if (poRes && poRes.success && poRes.data) {
					this.poReportValues = {
						grand_total_sum: +poRes.data.grand_total_sum,
						record_count: +((poRes.data.record_count != null) ? poRes.data.record_count : 0)
					}
					this.totalPOValue = this.poReportValues.grand_total_sum
				}

				if (changeOrderRes && changeOrderRes.success && changeOrderRes.data && Array.isArray(changeOrderRes.data))
					this.totalChangeOrderValue = changeOrderRes.data
						.filter(co => co.is_finalized == 1 && co.quote_status == 'Accepted')
						.reduce((acc, co) => acc + +(co.pretax_total || 0), 0)
				else
					this.totalChangeOrderValue = 0

				if (expensePORes && expensePORes.success && expensePORes.data && expensePORes.data.grand_total_sum != null)
					this.totalPOInvoiceValue = +expensePORes.data.grand_total_sum
				else
					this.totalPOInvoiceValue = 0

				if (this.activeBid != null) {
					let changeOrderLabour = this.computeChangeOrderLabour()
					this.budgetedLabour = (+this.budgetedLabour + changeOrderLabour).round(2)
				}

				this.showChart = true
			})
		}
	},
	created() {
		if (this.project && "id" in this.project) {
			this.fetchData()
		}
	}
}
</script>

<style scoped>
.kpi-layout {
	display: flex;
	gap: 1rem;
	align-items: stretch;
}
.kpi-lead {
	flex: 0 0 auto;
	min-width: 180px;
	display: flex;
	flex-direction: column;
	min-height: 0;
}
.kpi-lead :deep(.card) {
	flex: 1;
	min-height: 0;
}
.kpi-grid {
	display: grid;
	grid-template-columns: 1fr;
	grid-auto-flow: row;
	gap: 1rem;
	flex: 1;
}
@media (min-width: 992px) {
	.kpi-grid {
		grid-template-columns: repeat(2, 1fr);
		grid-template-rows: auto auto auto;
		grid-auto-flow: column;
	}
	.kpi-grid--2rows {
		grid-template-rows: auto auto;
	}
}
.chart-col {
	display: flex;
	flex-direction: column;
	min-height: 0;
}
.chart-fill {
	flex: 1;
	min-height: 280px;
	display: flex;
	flex-direction: column;
}
.chart-fill :deep(.chart-container) {
	flex: 1;
	min-height: 280px;
}

</style>